/**
 * g.shortcuts - context-stack keyboard shortcuts.
 *
 * Shortcuts belong to CONTEXTS stacked on top of each other. A keydown walks
 * the stack from the top: the first context that declares the key handles it;
 * a `blocking` context stops the walk even for keys it doesn't declare (this
 * is how open modals/offcanvases silence everything under them - genus pushes
 * one automatically on show.bs.modal / show.bs.offcanvas).
 *
 *   const ctx = g.shortcuts.push('lister:users', {
 *       'ArrowDown': { run: (e) => ..., label: 'Next row' },
 *       'e':         { run: (e) => ..., label: 'Edit row' },
 *       'Enter':     { label: 'Save' },              // no run = display-only hint
 *       'ctrl+k':    '#searchButton',                // selector -> click it
 *   }, { label: 'Users', blocking: false, root: el });
 *   ctx.set(keys)                 // swap the key map in place
 *   g.shortcuts.pop(ctx)          // or pop('lister:users')
 *   g.shortcuts.active()          // stack top-down (feeds the ? panel)
 *   g.shortcuts.help(true|false)  // the ? panel, also on the ? key
 *
 * Key specs: single characters ('e', '?', case-insensitive, shift ignored),
 * named keys ('Enter', 'Escape', 'ArrowUp', 'Space', 'PageDown', ...) and
 * 'ctrl+'/'alt+' prefixes (cmd counts as ctrl). While the user types in a
 * field only 'Escape' and 'ctrl+*' shortcuts fire. A handler may return
 * `false` to say "not handled here" - the walk then continues below it.
 *
 * THE ? PANEL: `?` (works even over blocking contexts) toggles a small panel
 * in the top-right corner - z-index above modal backdrops - listing the
 * LABELED keys of the active contexts top-down, stopping at the first
 * blocking one. It live-updates while open (open a modal and it switches to
 * the modal's keys). Close: `?` again (toggle) or its x button - deliberately
 * NOT Escape, which keeps acting on what is under the panel (open dialogs).
 *
 * MODAL / OFFCANVAS HINTS: the auto-pushed blocking context reads
 * `key-hint="Enter"` attributes inside the dialog (label: key-hint-label >
 * title > aria-label > text). Hinted keys also RUN (click the element) when
 * the focus is not on a native control - so Enter can confirm a dialog - and
 * an Escape=Close hint is always listed (Bootstrap itself handles Escape).
 *
 * OPT-IN PER SITE: the whole system fires only with `settings.shortcuts`
 * on (site config `site.shortcuts` — DEFAULT OFF). Disabled = no keydown
 * dispatch at all, the built-in `?` help key included; push/pop/active
 * keep working inertly (libs may register contexts unconditionally) and a
 * programmatic g.shortcuts.help() call still shows the panel.
 */
import { settings } from './core.js';
import { translate } from './translate.js';

const stack = [];
let uid = 0;

// ---- key spec normalization -------------------------------------------------

function normalizeSpec(spec) {
	const parts = String(spec).split('+');
	let key = parts.pop();
	if (key === ' ') key = 'Space';
	if (key.length === 1) key = key.toLowerCase();
	const mods = new Set(parts.map((p) => p.trim().toLowerCase()));
	return (mods.has('ctrl') || mods.has('cmd') || mods.has('meta') ? 'ctrl+' : '') + (mods.has('alt') ? 'alt+' : '') + (mods.has('shift') && key.length > 1 ? 'shift+' : '') + key;
}

function specOf(e) {
	let key = e.key;
	if (key === ' ') key = 'Space';
	if (key.length === 1) key = key.toLowerCase();
	// Shift is ignored for printable characters ('?' already arrives as '?').
	return (e.ctrlKey || e.metaKey ? 'ctrl+' : '') + (e.altKey ? 'alt+' : '') + (e.shiftKey && key.length > 1 ? 'shift+' : '') + key;
}

const isTyping = (e) => {
	const t = e.target;
	return !!(t && t.matches && (t.isContentEditable || t.matches('input, textarea, select')));
};

// ---- contexts ----------------------------------------------------------------

function normalizeKeys(keys, root) {
	const map = {};
	for (const spec of Object.keys(keys || {})) {
		let def = keys[spec];
		if (typeof def === 'function') def = { run: def };
		else if (typeof def === 'string') {
			const selector = def;
			def = { run: () => { const el = (root || document).querySelector(selector); if (!el) return false; el.click(); } };
		}
		map[normalizeSpec(spec)] = def;
	}
	return map;
}

function push(name, keys, opts = {}) {
	const ctx = {
		id: ++uid,
		name: String(name || 'context'),
		label: opts.label || '',
		blocking: !!opts.blocking,
		root: opts.root || null,
		keys: normalizeKeys(keys, opts.root),
		set(newKeys) { ctx.keys = normalizeKeys(newKeys, ctx.root); refreshHelp(); },
	};
	stack.push(ctx);
	refreshHelp();
	return ctx;
}

function pop(ref) {
	for (let i = stack.length - 1; i >= 0; i--) {
		if (stack[i] === ref || stack[i].name === ref || stack[i].id === ref) {
			const removed = stack.splice(i, 1)[0];
			refreshHelp();
			return removed;
		}
	}
	return null;
}

const active = () => stack.slice().reverse().map((c) => ({ name: c.name, label: c.label, blocking: c.blocking, keys: Object.keys(c.keys).map((k) => ({ key: k, label: c.keys[k].label || '' })) }));

// ---- dispatcher ----------------------------------------------------------------

function dispatch(e) {
	// the per-site switch (site.shortcuts, default OFF) — read at dispatch
	// time, so g.init (which runs after the libs load) decides
	if (!settings.shortcuts) return;
	if (e.defaultPrevented) return;
	// Chrome autofill (and some IME/extension paths) dispatches synthetic
	// keydown events WITHOUT a key — nothing to match, never a crash
	if (typeof e.key !== 'string' || e.key === '') return;
	const spec = specOf(e);
	const typing = isTyping(e);
	if (typing && spec !== 'Escape' && !spec.startsWith('ctrl+')) return;

	for (let i = stack.length - 1; i >= 0; i--) {
		const ctx = stack[i];
		const def = ctx.keys[spec];
		if (def && def.run) {
			const result = def.run(e, ctx);
			if (result !== false) {
				e.preventDefault();
				return;
			}
		}
		if (ctx.blocking) break;
	}
	// Nothing claimed it: the built-in help key (works over blocking contexts
	// too - the panel must be reachable from inside a modal).
	if (spec === '?' && !typing) {
		e.preventDefault();
		help();
	}
}

// ---- automatic modal / offcanvas blocking + key-hint scan -----------------------

// The dialog's keys: [key-hint] elements inside it. Hinted keys also RUN
// (click their element), but only when the browser would not natively act on
// the keypress (focused buttons/links handle Enter themselves).
function dialogKeys(root) {
	const keys = {};
	for (const el of root.querySelectorAll('[key-hint]')) {
		const spec = el.getAttribute('key-hint');
		if (!spec || keys[spec]) continue;
		const label = el.getAttribute('key-hint-label') || el.title || el.getAttribute('aria-label') || el.textContent.trim();
		keys[spec] = { label, run: (e) => {
			if (e.target.closest && e.target.closest('button, a, input, select, textarea, [role="button"]')) return false;
			const target = root.querySelector(`[key-hint="${CSS.escape(spec)}"]`);
			if (!target || target.disabled) return false;
			target.click();
		} };
	}
	if (!keys['Escape']) keys['Escape'] = { label: translate('Close') }; // display-only; Bootstrap handles it
	return keys;
}

function dialogLabel(root) {
	const title = root.querySelector('.modal-title, .offcanvas-title');
	return (title && title.textContent.trim()) || root.getAttribute('aria-label') || translate('Dialog');
}

const overlayCtx = new WeakMap();
function watchOverlays() {
	for (const kind of ['modal', 'offcanvas']) {
		document.addEventListener(`show.bs.${kind}`, (e) => {
			if (!overlayCtx.has(e.target)) overlayCtx.set(e.target, push(kind, dialogKeys(e.target), { blocking: true, root: e.target, label: dialogLabel(e.target) }));
		});
		document.addEventListener(`hidden.bs.${kind}`, (e) => {
			const ctx = overlayCtx.get(e.target);
			if (ctx) { pop(ctx); overlayCtx.delete(e.target); }
		});
	}
}

// ---- the ? panel (top-right corner, above everything) ----------------------------

let helpEl = null;
let helpCtx = null;

function helpMarkup() {
	const kbd = (k) => k.split('+').map((p) => `<kbd>${({ ArrowUp: '&uarr;', ArrowDown: '&darr;', ArrowLeft: '&larr;', ArrowRight: '&rarr;', ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift' })[p] || p}</kbd>`).join(' ');
	const groups = [];
	for (const ctx of active()) {
		const rows = ctx.keys.filter((k) => k.label);
		if (!rows.length) { if (ctx.blocking) break; continue; }
		groups.push(`<div class="genus-keys-group">${ctx.label ? `<div class="genus-keys-group-title">${ctx.label}</div>` : ''}${rows.map((k) => `<div class="genus-keys-row"><span class="genus-keys-keys">${kbd(k.key)}</span><span class="genus-keys-label">${k.label}</span></div>`).join('')}</div>`);
		if (ctx.blocking) break;
	}
	if (!groups.length) groups.push(`<div class="genus-keys-none">${translate('No shortcuts available here.')}</div>`);
	return `<div class="genus-keys-title"><span>${translate('Keyboard shortcuts')}</span><button type="button" class="genus-keys-close" aria-label="${translate('Close')}">&times;</button></div><div class="genus-keys-body">${groups.join('')}</div>`;
}

// Re-render the open panel whenever the stack changes (push/pop/set) - the
// panel is LIVE: open a modal while it shows and it switches to the modal's
// keys, close the modal and the page context comes back.
function refreshHelp() {
	if (helpEl) helpEl.innerHTML = helpMarkup();
}

function help(show) {
	const open = !!helpEl;
	if (show === undefined) show = !open;
	if (show === open) return;
	if (!show) {
		if (helpCtx) pop(helpCtx);
		helpCtx = null;
		if (helpEl) helpEl.remove();
		helpEl = null;
		return;
	}
	helpEl = document.createElement('div');
	helpEl.className = 'genus-keys';
	helpEl.setAttribute('role', 'dialog');
	helpEl.setAttribute('aria-label', translate('Keyboard shortcuts'));
	helpEl.innerHTML = helpMarkup();
	// Never take focus: with a modal open, any focus movement outside it makes
	// Bootstrap's focus trap yank the focus back (onto the modal's x). Keeping
	// clicks focus-free leaves the focus exactly where the user had it.
	helpEl.addEventListener('mousedown', (e) => e.preventDefault());
	helpEl.addEventListener('click', (e) => { if (e.target.closest('.genus-keys-close')) help(false); });
	document.body.appendChild(helpEl);
	// Non-blocking: the page keeps reacting to its shortcuts while the panel
	// is open (it is a cheat-sheet, not a modal). No Escape here on purpose -
	// Escape belongs to whatever is under the panel (e.g. it closes the open
	// modal); the panel closes only on ? (toggle) or its x button.
	helpCtx = push('shortcuts-help', { '?': { run: () => help(false) } });
}

// ---- public API --------------------------------------------------------------------

export const shortcuts = { push, pop, active, help };

export function initShortcuts() {
	document.addEventListener('keydown', dispatch);
	watchOverlays();
}
